import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { TherapyPlate } from "@/components/gisela/TherapyPlate"
import { useLocalePath } from "@/lib/locale-path"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface TherapyItem {
  id: string
  name: string
  /** What people call it — "CBT" out of "Cognitive Behavioural Therapy (CBT)". */
  short_name: string
  blurb: string
  path: string
  /** The room this row's name is set over, or null while none is placed. */
  photo?: PlacementPhoto | null
}

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    items: TherapyItem[]
    // The closing pair every page of hers ends on.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  /** The home page's tile slot — one card per page of her site but this one. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  meta?: PageMeta
}

// The index of the therapies Gisela practises — the same flat list the nav
// dropdown shows, a row apiece and open. Unlike the conditions index there
// are no groups and no listed-only entries: five approaches, every one with
// a page of its own, so nothing here is worth hiding behind a click. Each
// row is a plate — a room of hers under one of the brand's colours with the
// therapy's short name set over it — then the full name and what it is for,
// which is both the menu and the answer to "what does she actually do?".
export default function TherapiesIOffer({ content, tile_photos = {}, contact_photo, meta }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.therapies
  const lp = useLocalePath()
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">{t.eyebrow}</p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* The therapies */}
        <section className="bg-cream py-10 lg:py-14">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            {content.items.map((item, i) => (
              <div
                key={item.id}
                id={item.id}
                className={`grid scroll-mt-24 grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-6 gap-y-4 py-8 sm:grid-cols-[8.5rem_minmax(0,5fr)_minmax(0,7fr)] sm:gap-x-10 lg:py-10 ${
                  i === 0 ? "" : "border-t border-hairline"
                }`}
              >
                {/* The plate: a room of hers under a colour, with the name
                    over it. Its own colour per row, so the column reads as
                    five places rather than one block. */}
                <a href={lp(item.path)} tabIndex={-1} aria-hidden="true" className="block">
                  <TherapyPlate photo={item.photo} name={item.short_name} fullName={item.name} index={i} />
                </a>

                <div>
                  <h2>
                    <a href={lp(item.path)}>{item.name}</a>
                  </h2>
                  <p className="mt-2">
                    <a href={lp(item.path)} className="text-sm font-medium text-teal">
                      {t.how_link}
                    </a>
                  </p>
                </div>

                {/* On a phone the description reads under the name rather than
                    beside it — a third column at that width is a word wide. */}
                <div className="body-content col-span-2 text-ink-muted sm:col-span-1">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Keep any links inside the blurb on the visitor's language.
                      a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
                    }}
                  >
                    {item.blurb}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* The close every page of hers ends on — the invitation, the tiles
            out of it, then a room beside the short form — in this page's own
            words: a reader who has just read the list of approaches is asking
            which one is theirs, and the answer is a conversation. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page's own tile is left out — the reader is on it. */}
        <GiselaPageTiles photos={tile_photos} exclude={["/therapies-i-offer"]} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}
